// Builds schema.org Product JSON-LD for product detail pages.
// Only fields that exist in the data are included — empty values are dropped.

const SITE_URL = "https://www.extremeperformance.com";
const ORGANIZATION_ID = `${SITE_URL}/#organization`;

const hasValue = (value) =>
    value !== null && value !== undefined && String(value).trim() !== "";

const property = (name, value, unitText) => {
    if (!hasValue(value)) return null;
    const prop = { "@type": "PropertyValue", name, value: String(value).trim() };
    if (unitText) prop.unitText = unitText;
    return prop;
};

// Return policy as per the Terms & Conditions page: 5-day return window,
// restocking fee paid by the customer.
const returnPolicy = {
    "@type": "MerchantReturnPolicy",
    applicableCountry: "US",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 5,
    returnMethod: "https://schema.org/ReturnByMail",
    returnFees: "https://schema.org/ReturnFeesCustomerResponsibility",
};

// Free shipping applies to wheels and wheel/tire packages within the US.
const freeShipping = {
    "@type": "OfferShippingDetails",
    shippingRate: { "@type": "MonetaryAmount", value: 0, currency: "USD" },
    shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
};

// steel_wheel column is unreliable, so material comes from construction_type.
// Values that don't clearly say steel/aluminum (e.g. "Forged", "Cast") are skipped.
const wheelMaterial = (constructionType) => {
    if (!hasValue(constructionType)) return null;
    const value = String(constructionType).trim();
    if (/steel/i.test(value) || /^st$/i.test(value)) return "Steel";
    if (/alumin/i.test(value) || /^al$/i.test(value)) return "Aluminum";
    return null;
};

// data = one row from /api/getSingleWheelDetails/:sku
const buildWheelProductSchema = (data, { url, name, description }) => {
    if (!data || !data.sku) return null;

    const price = data.map_usd && data.map_usd > 0 ? data.map_usd : data.msrp_usd;
    const images = [data.image_url1, data.image_url2, data.image_url3, data.image_url4].filter(hasValue);
    const upc = hasValue(data.upc) ? String(data.upc).trim() : "";

    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${url}#product`,
        url,
        name,
        description,
        sku: data.sku,
        mpn: data.sku,
        category: "Wheels",
    };

    if (images.length > 0) schema.image = images;
    if (/^\d{12}$/.test(upc)) schema.gtin12 = upc;
    if (/^\d{13}$/.test(upc)) schema.gtin13 = upc;

    if (hasValue(data.brand_desc)) {
        schema.brand = { "@type": "Brand", name: data.brand_desc };
        if (hasValue(data.brand_id)) {
            schema.brand.url = encodeURI(`${SITE_URL}/wheels/${data.brand_id}/${data.brand_desc}`);
        }
    }

    if (hasValue(data.fancy_finish_desc)) schema.color = data.fancy_finish_desc;

    const material = wheelMaterial(data.construction_type);
    if (material) schema.material = material;

    const additionalProperty = [
        property("Diameter", data.diameter, "in"),
        property("Width", data.width, "in"),
        property("Bolt Pattern", data.bolt_pattern_metric),
        property("Offset", data.wheel_offset, "mm"),
        property("Center Bore", data.centerbore, "mm"),
        data.load_rating_standard > 0 ? property("Load Rating", data.load_rating_standard, "lbs") : null,
    ].filter(Boolean);
    if (additionalProperty.length > 0) schema.additionalProperty = additionalProperty;

    // Skip the offer entirely when there is no valid price
    if (price && Number(price) > 0) {
        schema.offers = {
            "@type": "Offer",
            url,
            priceCurrency: "USD",
            price: Number(price).toFixed(2),
            itemCondition: "https://schema.org/NewCondition",
            availability: data.quantity > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            seller: { "@id": ORGANIZATION_ID },
            hasMerchantReturnPolicy: returnPolicy,
        };
        // Page shows "Free Shipping" on wheels only when msrp_usd >= 100
        if (Number(data.msrp_usd) >= 100) schema.offers.shippingDetails = freeShipping;
    }
    // No price = no offers. Google needs offers/review/aggregateRating, so skip Product schema
    if (!schema.offers) return null;

    return schema;
};

// Treat 0 / "0" / junk DB values as missing for tire specs
const tireValue = (value) =>
    hasValue(value) && String(value).trim() !== "0" && !String(value).includes("NULL::") ? value : null;

// Features come as newline-separated lines; flatten into plain text
const featuresToText = (features) => {
    if (!hasValue(features)) return "";
    return String(features)
        .replace(/<[^>]*>/g, " ")
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean)
        .join(". ");
};

// data = one row from /api/tires/getSingleTiresDetails/:item_id
const buildTireProductSchema = (data, { url, name }) => {
    if (!data || !data.part_number) return null;

    const images = hasValue(data.images) ? String(data.images).split(",").map((img) => img.trim()).filter(Boolean) : [];
    const barcode = hasValue(data.barcode) ? String(data.barcode).trim() : "";
    const description = featuresToText(data.features) || name;

    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${url}#product`,
        url,
        name,
        description,
        sku: data.part_number,
        mpn: data.part_number,
        category: "Tires",
    };

    if (images.length > 0) schema.image = images;
    if (/^\d{12}$/.test(barcode)) schema.gtin12 = barcode;
    if (/^\d{13}$/.test(barcode)) schema.gtin13 = barcode;

    if (hasValue(data.brand)) {
        schema.brand = { "@type": "Brand", name: data.brand };
        if (hasValue(data.brand_id)) {
            schema.brand.url = encodeURI(`${SITE_URL}/tires/${data.brand_id}/${data.brand}`);
        }
    }

    // Warranty is shown on the page only when it is a 5-digit mileage
    const warranty = hasValue(data.warranty) && String(data.warranty).length === 5 ? data.warranty : null;

    const additionalProperty = [
        property("Size", tireValue(data.size)),
        property("Load Index", tireValue(data.loadindex)),
        property("Speed Rating", tireValue(data.speedrating)),
        property("Load Range", tireValue(data.load_range)),
        property("Sidewall", tireValue(data.sidewall)),
        property("Max Load Capacity", tireValue(data.max_load_capacity), "lbs"),
        property("Max Inflation Pressure", tireValue(data.inflation_pressure), "psi"),
        property("Tread Depth", tireValue(data.tread_depth) ? parseFloat(data.tread_depth) : null, "/32 in"),
        property("Treadwear", tireValue(data.treadwear)),
        property("Mileage Warranty", warranty, "miles"),
    ].filter(Boolean);
    if (additionalProperty.length > 0) schema.additionalProperty = additionalProperty;

    // Same stock logic as the page: status 0/3 = Back Order
    const isBackOrder = data.status === 0 || data.status === 3;
    const availability = isBackOrder
        ? "https://schema.org/BackOrder"
        : data.quantity > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock";

    // Skip the offer entirely when there is no valid price
    if (data.price && Number(data.price) > 0) {
        schema.offers = {
            "@type": "Offer",
            url,
            priceCurrency: "USD",
            price: Number(data.price).toFixed(2),
            itemCondition: "https://schema.org/NewCondition",
            availability,
            seller: { "@id": ORGANIZATION_ID },
            hasMerchantReturnPolicy: returnPolicy,
        };
        // Page shows "Free Shipping" on tires only when price >= 100
        if (Number(data.price) >= 100) schema.offers.shippingDetails = freeShipping;
    }
    // No price = no offers. Google needs offers/review/aggregateRating, so skip Product schema
    if (!schema.offers) return null;

    return schema;
};


// Rough Country stock comes as text in `availability`:
// "In Stock", "Out Of Stock", "ETA ..." or "Please allow 2-3 days to ship" (special order)
const roughCountryAvailability = (availability) => {
    const value = hasValue(availability) ? String(availability).trim() : "";
    if (value === "In Stock") return "https://schema.org/InStock";
    if (value === "Out Of Stock") return "https://schema.org/OutOfStock";
    return "https://schema.org/BackOrder";
};

// data = product from /api/rough/getRoughCountryProductDetail/:sku
const buildRoughCountryProductSchema = (data, { url, name, description }) => {
    if (!data || !data.sku) return null;

    const images = [data.image_1, data.image_2, data.image_3, data.image_4, data.image_5, data.image_6]
        .filter(hasValue);
    const upc = hasValue(data.upc) ? String(data.upc).trim() : "";
    const productDescription = hasValue(data.product_description)
        ? String(data.product_description).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
        : "";

    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${url}#product`,
        url,
        name,
        description: productDescription || description,
        sku: data.sku,
        mpn: data.sku,
    };

    if (hasValue(data.category)) schema.category = data.category;
    if (images.length > 0) schema.image = images;
    if (/^\d{12}$/.test(upc)) schema.gtin12 = upc;
    if (/^\d{13}$/.test(upc)) schema.gtin13 = upc;

    if (hasValue(data.manufacturer)) {
        schema.brand = { "@type": "Brand", name: data.manufacturer };
        if (data.manufacturer === "Rough Country") schema.brand.url = `${SITE_URL}/rough-country`;
    }

    // Free shipping is not shown on Rough Country pages
    if (data.price && Number(data.price) > 0) {
        schema.offers = {
            "@type": "Offer",
            url,
            priceCurrency: "USD",
            price: Number(data.price).toFixed(2),
            itemCondition: "https://schema.org/NewCondition",
            availability: roughCountryAvailability(data.availability),
            seller: { "@id": ORGANIZATION_ID },
            hasMerchantReturnPolicy: returnPolicy,
        };
    }
    // No price = no offers. Google needs offers/review/aggregateRating, so skip Product schema
    if (!schema.offers) return null;

    return schema;
};

// Same price formula as components/DetailSection/index.js (accessories).
// Keep both in sync if the pricing rules change.
const accessoryPrice = (priceData) => {
    if (!priceData) return 0;

    const cost = parseFloat(priceData.cost);
    const map = parseFloat(priceData.map);
    const retail = parseFloat(priceData.retails);
    const jobber = parseFloat(priceData.jobber);
    const extra = (parseFloat(priceData.surcharge) || 0) + (parseFloat(priceData.corecharge) || 0);

    if (map > 0) return map + extra;
    if (retail > 0) {
        const calculatedRetailPrice = retail - (0.1 * retail);
        return (cost * 1.1 < calculatedRetailPrice ? calculatedRetailPrice : cost * 1.1) + extra;
    }
    if (jobber > 0) return (jobber > cost * 1.1 ? jobber : cost * 1.1) + extra;
    return cost * 1.1 + extra;
};

// Same image order as components/Slider/index.js: S3 images first, then vendor images
const accessoryImages = (related) => {
    if (!related) return [];
    const S3_URL = "https://extremewheelsdevelopment.s3-accelerate.amazonaws.com/";
    const isImage = (img) => img !== "" && !/\.pdf$/i.test(img) && !/youtube\.com|youtu\.be/i.test(img);

    if (hasValue(related.s3_images)) {
        return String(related.s3_images).split(",").map((img) => img.trim()).filter(isImage).map((img) => S3_URL + img);
    }
    if (hasValue(related.images)) {
        return String(related.images).split(",").map((img) => img.trim()).filter(isImage);
    }
    return [];
};

// data = full response from /api/getItemDetails/:item_id

const buildAccessoryProductSchema = (data, { url, name }) => {
    const item = data?.itemsData?.[0];
    if (!item || !item.part_number) return null;

    const related = data.itemRelatedData?.[0];
    const images = accessoryImages(related);

    const barcode = hasValue(item.barcode)
        ? String(item.barcode).trim()
        : "";

    const relatedDescription = related && hasValue(related.description)
        ? String(related.description)
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim()
        : "";

    const schema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "@id": `${url}#product`,
        url,
        name,
        description: relatedDescription || name,
        sku: item.part_number,
        mpn: hasValue(item.mfr_part_number)
            ? item.mfr_part_number
            : item.part_number,
    };

    if (hasValue(item.category_name)) {
        schema.category = item.category_name;
    }

    if (images.length > 0) schema.image = images;
    if (/^\d{12}$/.test(barcode)) schema.gtin12 = barcode;
    if (/^\d{13}$/.test(barcode)) schema.gtin13 = barcode;

    if (hasValue(item.brand)) {
        schema.brand = {
            "@type": "Brand",
            name: item.brand,
        };
    }

    // Fetch price from itemsData and convert it to a number
    const price = Number(item.price);

    if (Number.isFinite(price) && price > 0) {
        schema.offers = {
            "@type": "Offer",
            url,
            priceCurrency: "USD",
            price: price.toFixed(2),
            itemCondition: "https://schema.org/NewCondition",
            availability: Number(item.stock) > 0
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            seller: { "@id": ORGANIZATION_ID },
            hasMerchantReturnPolicy: returnPolicy,
        };
    }

    // No price = no offers
    if (!schema.offers) return null;

    return schema;
};

// CommonJS exports so the SSR server (Node require) can load this file.
// Webpack still handles `import { ... } from "utils/productSchema"` on the client.
exports.buildWheelProductSchema = buildWheelProductSchema;
exports.buildTireProductSchema = buildTireProductSchema;
exports.buildRoughCountryProductSchema = buildRoughCountryProductSchema;
exports.buildAccessoryProductSchema = buildAccessoryProductSchema;